/**
 * Profile dropdown menu — user avatar, name, tenant context, and logout.
 *
 * Cross-References:
 *   - FR-UI-002: Top header profile menu
 *   - dashboard screen spec: Profile dropdown
 */
import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, ChevronDown } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useClickOutside } from '@/hooks/useClickOutside';

export function ProfileMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(menuRef, close);

  if (!user) return null;

  const initials = user.displayName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  async function handleLogout() {
    setOpen(false);
    await logout();
    navigate('/login');
  }

  return (
    <div className="profile-menu" ref={menuRef}>
      <button
        className="profile-menu__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="User menu"
      >
        <span className="profile-menu__avatar">{initials}</span>
        <span className="profile-menu__name">{user.displayName}</span>
        <ChevronDown size={14} className={`profile-menu__chevron${open ? ' profile-menu__chevron--open' : ''}`} />
      </button>

      {open && (
        <div className="profile-menu__dropdown" role="menu">
          <div className="profile-menu__info">
            <span className="profile-menu__email">{user.email}</span>
          </div>
          <div className="profile-menu__divider" />
          <button
            className="profile-menu__item"
            role="menuitem"
            onClick={() => { setOpen(false); navigate('/profile'); }}
          >
            <User size={14} />
            <span>My Profile</span>
          </button>
          <div className="profile-menu__divider" />
          <button
            className="profile-menu__item profile-menu__item--danger"
            role="menuitem"
            onClick={handleLogout}
          >
            <LogOut size={14} />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
